import apiClient from "@/api/client";
import type {
  DockerHost,
  Container,
  Stack,
  ContainerAction,
  StackAction,
  ContainerCreatePayload,
  DockerHostCreatePayload,
  StackCreatePayload,
  LiveContainer,
  DockerImage,
  DockerVolume,
  DockerNetwork,
  SystemDf,
  ContainerFiles,
} from "@/types/docker";

// --- hosts -----------------------------------------------------------------

export async function getDockerHosts(): Promise<DockerHost[]> {
  const { data } = await apiClient.get<DockerHost[]>("/docker/hosts");
  return data;
}

export async function createDockerHost(
  payload: DockerHostCreatePayload,
): Promise<DockerHost> {
  const { data } = await apiClient.post<DockerHost>("/docker/hosts", payload);
  return data;
}

export async function deleteDockerHost(hostId: number): Promise<void> {
  await apiClient.delete(`/docker/hosts/${hostId}`);
}

/**
 * Push the latest bundled agent onto a host and restart it (async).
 * Defaults to the fast path (code-only, no apt/pip). Pass full=true to force a
 * complete re-bootstrap when dependencies or Docker itself changed.
 */
export async function redeployHostAgent(
  hostId: number,
  full = false,
): Promise<void> {
  await apiClient.post(`/docker/hosts/${hostId}/agent/redeploy`, null, {
    params: { full },
  });
}

// --- containers ------------------------------------------------------------

export async function getContainers(hostId: number): Promise<Container[]> {
  const { data } = await apiClient.get<Container[]>(
    `/docker/hosts/${hostId}/containers`,
  );
  return data;
}

export async function createContainer(
  hostId: number,
  payload: ContainerCreatePayload,
): Promise<Container> {
  const { data } = await apiClient.post<Container>(
    `/docker/hosts/${hostId}/containers`,
    payload,
  );
  return data;
}

export async function containerAction(
  containerId: number,
  action: ContainerAction,
): Promise<Container> {
  const { data } = await apiClient.post<Container>(
    `/docker/containers/${containerId}/${action}`,
  );
  return data;
}

export async function deleteContainer(containerId: number): Promise<void> {
  await apiClient.delete(`/docker/containers/${containerId}`);
}

export interface ContainerCostEstimate {
  cpus: number;
  memory_mb: number;
  cpu_monthly: number;
  ram_monthly: number;
  total_monthly: number;
  currency: string;
}

export async function estimateContainerCost(
  cpus: number,
  memory_mb: number,
): Promise<ContainerCostEstimate> {
  const { data } = await apiClient.post<ContainerCostEstimate>(
    "/docker/billing/estimate",
    { cpus, memory_mb },
  );
  return data;
}

export interface DockerBillingSummary {
  total_monthly: number;
  currency: string;
  containers: {
    container_id: number;
    name: string;
    cpus: number;
    memory_mb: number;
    monthly_cost: number;
  }[];
}

export async function getDockerBillingSummary(): Promise<DockerBillingSummary> {
  const { data } = await apiClient.get<DockerBillingSummary>(
    "/docker/billing/summary",
  );
  return data;
}

export async function issueLogsTicket(containerId: number): Promise<string> {
  const { data } = await apiClient.post<{ ticket: string }>(
    `/docker/containers/${containerId}/logs/ticket`,
  );
  return data.ticket;
}

export async function issueStatsTicket(containerId: number): Promise<string> {
  const { data } = await apiClient.post<{ ticket: string }>(
    `/docker/containers/${containerId}/stats/ticket`,
  );
  return data.ticket;
}

/** Build the ws(s):// URL for a backend WebSocket path under /api/v1. */
export function dockerWsUrl(path: string, ticket: string): string {
  const base = (import.meta.env.VITE_API_URL as string) || "/api/v1";
  const httpBase = base.startsWith("http")
    ? base
    : window.location.origin + base;
  return `${httpBase.replace(/^http/, "ws")}${path}?ticket=${encodeURIComponent(ticket)}`;
}

// --- live explorer ---------------------------------------------------------

export async function getLiveContainers(
  hostId: number,
): Promise<LiveContainer[]> {
  const { data } = await apiClient.get<LiveContainer[]>(
    `/docker/hosts/${hostId}/live/containers`,
  );
  return data;
}

export async function inspectLiveContainer(
  hostId: number,
  cid: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get<Record<string, unknown>>(
    `/docker/hosts/${hostId}/live/containers/${cid}/inspect`,
  );
  return data;
}

export async function getContainerFiles(
  hostId: number,
  cid: string,
  path: string,
): Promise<ContainerFiles> {
  const { data } = await apiClient.get<ContainerFiles>(
    `/docker/hosts/${hostId}/live/containers/${cid}/files`,
    { params: { path } },
  );
  return data;
}

export async function liveContainerAction(
  hostId: number,
  cid: string,
  action: ContainerAction,
): Promise<LiveContainer> {
  const { data } = await apiClient.post<LiveContainer>(
    `/docker/hosts/${hostId}/live/containers/${cid}/${action}`,
  );
  return data;
}

export async function deleteLiveContainer(
  hostId: number,
  cid: string,
): Promise<void> {
  await apiClient.delete(`/docker/hosts/${hostId}/live/containers/${cid}`);
}

export async function getHostImages(hostId: number): Promise<DockerImage[]> {
  const { data } = await apiClient.get<DockerImage[]>(
    `/docker/hosts/${hostId}/images`,
  );
  return data;
}

export async function pullHostImage(
  hostId: number,
  repository: string,
  tag?: string,
): Promise<DockerImage> {
  const { data } = await apiClient.post<DockerImage>(
    `/docker/hosts/${hostId}/images/pull`,
    null,
    { params: { repository, tag } },
  );
  return data;
}

export async function deleteHostImage(
  hostId: number,
  imageId: string,
  force = false,
): Promise<void> {
  await apiClient.delete(`/docker/hosts/${hostId}/images/${imageId}`, {
    params: { force },
  });
}

export async function pruneHostImages(hostId: number): Promise<void> {
  await apiClient.post(`/docker/hosts/${hostId}/images/prune`);
}

export async function getHostVolumes(hostId: number): Promise<DockerVolume[]> {
  const { data } = await apiClient.get<DockerVolume[]>(
    `/docker/hosts/${hostId}/volumes`,
  );
  return data;
}

export async function deleteHostVolume(
  hostId: number,
  name: string,
  force = false,
): Promise<void> {
  await apiClient.delete(`/docker/hosts/${hostId}/volumes/${name}`, {
    params: { force },
  });
}

export async function pruneHostVolumes(hostId: number): Promise<void> {
  await apiClient.post(`/docker/hosts/${hostId}/volumes/prune`);
}

export async function getHostNetworks(
  hostId: number,
): Promise<DockerNetwork[]> {
  const { data } = await apiClient.get<DockerNetwork[]>(
    `/docker/hosts/${hostId}/networks`,
  );
  return data;
}

export async function pruneHostNetworks(hostId: number): Promise<void> {
  await apiClient.post(`/docker/hosts/${hostId}/networks/prune`);
}

export async function getHostSystemDf(hostId: number): Promise<SystemDf> {
  const { data } = await apiClient.get<SystemDf>(
    `/docker/hosts/${hostId}/system/df`,
  );
  return data;
}

/** Issue a host-scoped stream ticket (logs/stats/exec) for a live container. */
export async function issueHostStreamTicket(
  hostId: number,
  cid: string,
  kind: "logs" | "stats" | "exec",
): Promise<string> {
  const { data } = await apiClient.post<{ ticket: string }>(
    `/docker/hosts/${hostId}/live/containers/${cid}/${kind}/ticket`,
  );
  return data.ticket;
}

// --- stacks ----------------------------------------------------------------

export async function getStacks(hostId: number): Promise<Stack[]> {
  const { data } = await apiClient.get<Stack[]>(`/docker/hosts/${hostId}/stacks`);
  return data;
}

export async function deployStack(
  hostId: number,
  payload: StackCreatePayload,
): Promise<Stack> {
  const { data } = await apiClient.post<Stack>(
    `/docker/hosts/${hostId}/stacks`,
    payload,
  );
  return data;
}

export async function stackAction(
  stackId: number,
  action: StackAction,
): Promise<Stack> {
  const { data } = await apiClient.post<Stack>(
    `/docker/stacks/${stackId}/${action}`,
  );
  return data;
}

export async function deleteStack(
  stackId: number,
  volumes = false,
): Promise<void> {
  await apiClient.delete(`/docker/stacks/${stackId}`, { params: { volumes } });
}
